import { CheckCircleIcon } from "@heroicons/react/24/solid";
import { FC } from "react";

interface ValidationProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  className?: string;
}

const ValidationProgressIndicator: FC<ValidationProgressIndicatorProps> = ({
  currentStep,
  totalSteps,
  className = "",
}) => {
  const steps = [
    {
      id: 1,
      title: "Type de profil",
      description: "Particulier ou Entreprise",
      icon: "👤",
    },
    {
      id: 2,
      title: "Informations",
      description: "Données personnelles",
      icon: "📝",
    },
    {
      id: 3,
      title: "Documents",
      description: "Justificatifs requis",
      icon: "📄",
    },
    {
      id: 4,
      title: "Validation",
      description: "Finalisation",
      icon: "✅",
    },
  ];

  return (
    <div className={`w-full ${className}`}>
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">🎯 Validation de votre profil</h2>
        <p className="text-gray-600 dark:text-gray-400">
          Complétez ces étapes pour que votre espace soit validé par nos équipes
        </p>
        <div className="border-b-2 w-20 border-eventToBee mt-4 mx-auto"></div>
      </div>

      {/* Progress Bar */}
      <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-8">
        <div
          className="bg-gradient-to-r from-eventToBee to-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
          style={{ width: `${(currentStep / totalSteps) * 100}%` }}
        />
      </div>

      {/* Steps */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        {steps.map((step, index) => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          const isUpcoming = currentStep < step.id;

          return (
            <div
              key={step.id}
              className={`relative flex flex-col items-center p-4 rounded-xl transition-all duration-300 ${
                isCurrent
                  ? "bg-blue-50 dark:bg-blue-900/20 border-2 border-eventToBee"
                  : isCompleted
                    ? "bg-green-50 dark:bg-green-900/20 border-2 border-green-500"
                    : "bg-gray-50 dark:bg-gray-800 border-2 border-gray-200 dark:border-gray-700"
              }`}
            >
              {/* Step Number/Icon */}
              <div
                className={`w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all duration-300 ${
                  isCompleted ? "bg-green-500" : isCurrent ? "bg-eventToBee" : "bg-gray-300 dark:bg-gray-600"
                }`}
              >
                {isCompleted ? (
                  <CheckCircleIcon className="w-6 h-6 text-white" />
                ) : (
                  <span className={`text-lg ${isCurrent ? "text-white" : "text-gray-600 dark:text-gray-300"}`}>
                    {step.icon}
                  </span>
                )}
              </div>

              {/* Step Content */}
              <div className="text-center">
                <h3
                  className={`font-semibold text-sm mb-1 ${
                    isCurrent
                      ? "text-eventToBee"
                      : isCompleted
                        ? "text-green-600"
                        : "text-gray-500 dark:text-gray-400"
                  }`}
                >
                  {step.title}
                </h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">{step.description}</p>
              </div>

              {/* Current Step Badge */}
              {isCurrent && (
                <div className="absolute -top-2 -right-2">
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-eventToBee text-white">
                    En cours
                  </span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Current Step Info */}
      <div className="text-center">
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Étape {currentStep} sur {totalSteps} •
          <span className="ml-1 font-medium text-eventToBee">
            {Math.round((currentStep / totalSteps) * 100)}% complété
          </span>
        </p>
      </div>
    </div>
  );
};

export default ValidationProgressIndicator;
